/**
 * FinalisationDialog Component
 * Unified end-of-life dialog replacing separate Rollover and Terminate actions.
 * Shows per-investor financial summary (capital, interest, WHT, net value) and
 * lets user split each investor's net value into Rollover Suspense (held for
 * future rollover) vs Paid Out (disbursement). Creates a pending_changes entry
 * for approval, or executes immediately for matured/maturing contracts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  Radio,
  FormControlLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import ContractActionDialogBase from './ContractActionDialogBase';

// Format number with space thousand separator and period decimal
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Band heading separating "Payable to investor" from "Still outstanding" in the Financial Summary.
// The two bands answer different questions and their figures must never be read as addable.
function BandLabel({ children, sx }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        fontWeight: 700, color: 'text.disabled', mb: 1,
        '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider' },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// A summary figure. tabular-nums keeps columns of digits aligned; `hint` marks a figure that carries
// a tooltip so it is discoverable, and tabIndex on the caller makes it keyboard-reachable.
const Figure = React.forwardRef(function Figure(
  { children, bold, color, hint, sx, ...props }, ref
) {
  return (
    <Typography
      ref={ref}
      variant="body2"
      fontWeight={bold ? 'bold' : 'medium'}
      sx={{
        color, fontVariantNumeric: 'tabular-nums',
        // `hint` figures carry a tooltip. The dotted underline must hug the text (inline-block), but
        // the tile still needs the value on its OWN line under the caption — so the inline-block sits
        // inside a block wrapper rather than becoming the tile's only child. Without the wrapper the
        // value renders beside its label ("Accrued InterestSZL 124 530.90").
        ...(hint && {
          cursor: 'help',
          borderBottom: '1px dotted',
          borderColor: 'text.disabled',
          display: 'inline-block',
          '&:hover, &:focus-visible': { borderColor: 'primary.main' },
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
});

// One line inside a tooltip breakdown.
function TipRow({ k, v, bold, indent }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="caption" sx={{ pl: indent ? 1 : 0, opacity: indent ? 0.85 : 1 }}>{k}</Typography>
      <Typography variant="caption" fontWeight={bold ? 700 : 400} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {v}
      </Typography>
    </Box>
  );
}

function TipRule() {
  return <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.28)', my: 0.75 }} />;
}

// Currency input: formatted when blurred, freely editable while focused.
//
// While focused it keeps its OWN draft string rather than mirroring the numeric state. Without that,
// every keystroke round-tripped through parseFloat and any partial entry was destroyed: typing "." gave
// NaN and was dropped, so ".17" arrived as "17" — a 100x error on a money field, and the reason
// leading-decimal entry felt impossible. The draft also preserves a trailing "." ("0.") and a typed
// "0.0" while the user is still typing the cents.
//
// The parent is still told about every keystroke, so validation and the linked suspense/payout split
// stay live as you type; only the DISPLAYED text is local.
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [draft, setDraft] = useState(null);
  const focused = draft !== null;
  const showFormatted = !focused || disabled;

  const handleChange = (e) => {
    // Digits, one optional decimal point, nothing else. Spaces are stripped so a pasted
    // "1 000.50" is accepted.
    const next = e.target.value.replace(/\s/g, '');
    if (next !== '' && !/^\d*\.?\d*$/.test(next)) return;
    setDraft(next);
    onChange(next);
  };

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : draft}
      onChange={disabled ? undefined : handleChange}
      onFocus={(e) => {
        // Seed the draft from the current value and select it, so typing replaces rather than appends.
        const num = parseFloat(value);
        setDraft(isNaN(num) ? '' : String(num));
        requestAnimationFrame(() => e.target.select?.());
      }}
      onBlur={() => setDraft(null)}
      // Deliberately type="text" while focused, NOT type="number": a number input rejects the
      // intermediate "." outright in some browsers and silently discards a value it considers
      // invalid, which is the same class of problem this component exists to avoid.
      type="text"
      inputMode="decimal"
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

const FinalisationDialog = ({ open, onClose, contract, contractId: explicitContractId, onSubmitSuccess }) => {
  const contractId = explicitContractId || contract?.id;

  const [finalisationDate, setFinalisationDate] = useState('');
  const [reason, setReason] = useState('');
  const [investorRows, setInvestorRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // T3-107: contract-level residual split behind the Accrued Interest figure. Returned repeated on
  // every investor row by fn_contract_get_finalisation_summary, so read it from the first row only.
  const [residual, setResidual] = useState(null);

  // Set default finalisation date when dialog opens
  // For Matured: use maturity date. For Active: use today.
  useEffect(() => {
    if (open && contract) {
      if (contract.status === 'Matured' && contract.maturity_date) {
        setFinalisationDate(format(new Date(contract.maturity_date), 'yyyy-MM-dd'));
      } else {
        setFinalisationDate(format(new Date(), 'yyyy-MM-dd'));
      }
      setReason('');
      setInvestorRows([]);
      setError(null);
    }
  }, [open, contract]);

  // Fetch financial summary when date changes
  const fetchSummary = useCallback(async (date) => {
    if (!contractId || !date) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/contracts/${contractId}/finalisation-summary?finalisation_date=${date}`);
      const data = result.data || [];
      // T3-117: writeoff_amount is CONTRACT-level and repeated on every investor row, so it is
      // apportioned here by each investor's share of the interest — the residual arises on interest
      // lines, so interest is the basis that matches where it came from.
      //
      // T3-124: the rounding remainder goes to the investor with the LARGEST CAPITAL (ties broken by
      // lowest investor_id), not to the last row. Row order is an ORDER BY, not a contract — and the
      // SQL side uses exactly this rule for its own rounding dust
      // (amortizationService.js:4040-4046, "deliberately NOT 'the last investor'"). Two different
      // remainder rules in one dialog is the quiet kind of inconsistency this codebase keeps paying
      // for; the amounts differ by at most a cent but the rule is now statable in one sentence.
      const contractWriteoff = parseFloat(data[0]?.writeoff_amount) || 0;
      const totalInterest = data.reduce((s, r) => s + (parseFloat(r.accrued_interest) || 0), 0);
      const remainderIdx = data.reduce((best, r, i) => {
        const cap = parseFloat(r.original_capital) || 0;
        const bestCap = parseFloat(data[best].original_capital) || 0;
        if (cap > bestCap) return i;
        if (cap === bestCap && (r.investor_id ?? Infinity) < (data[best].investor_id ?? Infinity)) return i;
        return best;
      }, 0);
      // Two passes, deliberately: the remainder investor is not necessarily LAST in the array, so a
      // single accumulating pass would hand them a remainder computed before the rows after them had
      // contributed — over-allocating (measured: a 1,000 ceiling produced 1,066.67 when the largest
      // investor sorted first). Pass 1 prorates everyone else; pass 2 gives the remainder investor
      // whatever is left, so the total is exact for ANY row order.
      const proRated = data.map((inv, idx) => {
        if (idx === remainderIdx) return 0;
        const interest = parseFloat(inv.accrued_interest) || 0;
        return totalInterest > 0
          ? Math.round(contractWriteoff * (interest / totalInterest) * 100) / 100
          : 0;
      });
      const allocatedTotal = proRated.reduce((s, v) => s + v, 0);
      const rows = data.map((inv, idx) => {
        const woMax = idx === remainderIdx
          ? Math.round((contractWriteoff - allocatedTotal) * 100) / 100
          : proRated[idx];
        return {
          investor_id: inv.investor_id,
          investor_name: inv.investor_name,
          original_capital: parseFloat(inv.original_capital) || 0,
          accrued_interest: parseFloat(inv.accrued_interest) || 0,
          wht_rate: parseFloat(inv.wht_rate) || 0,
          wht_gross_up: inv.wht_gross_up || false,
          wht_amount: parseFloat(inv.wht_amount) || 0,
          // T3-126: the system-derived provision, kept alongside the (editable) wht_amount above.
          // The API stamps THIS onto the schedule anchor row, so an override of wht_amount yields a
          // variance row rather than silently agreeing with itself. Never edited in the UI.
          wht_computed_amount: parseFloat(inv.wht_amount) || 0,
          // T3-126: 'remit' (we withhold and remit — payout NET) | 'payout' (handed to the investor
          // for a third party to remit — payout GROSS). Defaults to today's behaviour.
          wht_treatment: 'remit',
          // T3-124: WHT owed by RATE on lines that store no provision, reported separately so it is
          // never applied silently. The operator actions it or leaves 0.
          wht_rate_fallback_amount: parseFloat(inv.wht_rate_fallback_amount) || 0,
          wht_provision_missing: inv.wht_provision_missing === true,
          net_value: parseFloat(inv.net_value) || 0,
          // Default: all to suspense. Consistent with writeoff_amount starting at 0 below — when the
          // write-off was instead pre-filled at its maximum, these two were seeded independently and
          // the dialog opened in an impossible state (1,000 written off AND the full net value
          // allocated) because no handler had run to reconcile them. Keep the two defaults in step.
          reserve_amount: parseFloat(inv.net_value) || 0,
          payout_amount: 0,
          // The most this investor may write off, and the amount actually being taken.
          //
          // STARTS AT ZERO, deliberately. It was previously pre-filled with the maximum, which meant
          // simply opening the dialog proposed forgiving money nobody had asked to forgive — and the
          // operator had to notice and undo it. A write-off is a decision; the default is not to make
          // it. writeoff_max is what they MAY write off, writeoff_amount is what they choose to.
          writeoff_max: Math.max(0, woMax),
          writeoff_amount: 0,
        };
      });
      setInvestorRows(rows);
      // Contract-level, identical on every row — take it from the first.
      const first = (result.data || [])[0];
      setResidual(first ? {
        shortPaid: parseFloat(first.interest_short_paid) || 0,
        notYetPaid: parseFloat(first.interest_not_yet_paid) || 0,
        overPaid: parseFloat(first.interest_over_paid) || 0,
        whtNotYetPaid: parseFloat(first.wht_not_yet_paid) || 0,
        accruedNotDue: parseFloat(first.interest_accrued_not_due) || 0,
        // T3-108: false when the breakdown cannot be trusted even though it still sums
        // correctly. Treat a missing field (pre-T3-108 database) as trustworthy so the
        // warning never fires spuriously on an un-migrated environment.
        reconciled: first.residual_reconciled !== false,
        // T3-117: contract-level, same shape as the residual figures above.
        writeoffEligible: parseFloat(first.writeoff_eligible_amount) || 0,
        writeoffAmount: parseFloat(first.writeoff_amount) || 0,
        writeoffWithinLimit: first.writeoff_within_limit === true,
        // T3-124: contract-level attribution quality. On a multi-investor contract whose schedule
        // rows carry no investor_id (the T3-113 unsplit shape) the per-investor Interest/WHT cannot
        // be derived and fall back to capital-share proration — the operator must be told.
        // A MISSING field means an un-migrated database, which is treated as complete so the warning
        // never fires spuriously — same defensive rule as residual_reconciled above.
        unattributedInterest: parseFloat(first.unattributed_interest) || 0,
        attributionComplete: first.investor_attribution_complete !== false,
        // T3-124: total WHT owed by rate where no provision is stored, across all investors.
        whtRateFallback: (result.data || []).reduce(
          (s, r) => s + (parseFloat(r.wht_rate_fallback_amount) || 0), 0),
        // Do the "of which" components actually sum to the interest they claim to break down?
        // Computed against the SUMMED interest because the components are contract-level while
        // accrued_interest is per-investor — on a multi-investor contract only the total compares.
        componentsReconcile: (() => {
          const parts = (parseFloat(first.interest_short_paid) || 0)
                      + (parseFloat(first.interest_not_yet_paid) || 0)
                      + (parseFloat(first.interest_accrued_not_due) || 0);
          const interest = data.reduce((s, r) => s + (parseFloat(r.accrued_interest) || 0), 0);
          return Math.abs(parts - interest) <= 0.011;
        })(),
      } : null);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load financial summary');
      setInvestorRows([]);
      setResidual(null);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (open && finalisationDate) {
      fetchSummary(finalisationDate);
    }
  }, [open, finalisationDate, fetchSummary]);

  // What is actually available to allocate for one investor: their net value LESS anything written
  // off. A write-off forgives part of what the investor is owed, so it must reduce the amount being
  // split between suspense and payout — otherwise the contract would be paid out in full AND record a
  // write-off, counting the same money twice. Declared before the handlers that use it.
  // T3-126: tax paid out to the investor for a THIRD PARTY to remit. Raises this row's allocatable
  // amount, because the tax leaves with the final payment instead of being withheld.
  // Gross-up means the payer absorbs the tax, so nothing was withheld and there is nothing to hand
  // over — always 0 there, whatever the treatment says.
  const whtPaidOutOf = (row) =>
    (row.wht_treatment === 'payout' && !row.wht_gross_up) ? (row.wht_amount || 0) : 0;

  // THE ONE ALLOCATABLE CEILING. Every handler below derives from this, and the API validates the
  // identical expression (routes/contracts.js finalise route).
  //   net_value − writeoff (forgiven, allocated to nobody) + wht paid out (leaves with the payout)
  // ⚠ Keep in step with the API. Two bases for one invariant is what produced OPEN-ITEMS #109,
  // where the API used gross net_value and every non-zero write-off 400'd.
  const allocatableOf = (row) =>
    Math.max(0, row.net_value - (row.writeoff_amount || 0) + whtPaidOutOf(row));

  // Handle suspense amount change for an investor
  const handleSuspenseChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const available = allocatableOf(row);
      row.reserve_amount = Math.max(0, Math.min(num, available));
      row.payout_amount = Math.max(0, available - row.reserve_amount);
      updated[index] = row;
      return updated;
    });
  };

  // Handle payout amount change for an investor
  const handlePayoutChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const available = allocatableOf(row);
      row.payout_amount = Math.max(0, Math.min(num, available));
      row.reserve_amount = Math.max(0, available - row.payout_amount);
      updated[index] = row;
      return updated;
    });
  };

  // T3-126: switch this investor's WHT between 'remit' (we withhold and remit — payout is NET) and
  // 'payout' (handed to the investor as part of the final payment for a third party to remit —
  // payout is GROSS). Re-splits the allocation because the allocatable amount has changed, keeping
  // the operator's existing suspense/payout proportion rather than resetting it — the same rule
  // handleWriteoffChange uses.
  const handleWhtTreatmentChange = (index, treatment) => {
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], wht_treatment: treatment };
      const available = allocatableOf(row);
      const prior = (updated[index].reserve_amount || 0) + (updated[index].payout_amount || 0);
      if (prior > 0.005) {
        const payoutShare = (updated[index].payout_amount || 0) / prior;
        row.payout_amount = Math.round(available * payoutShare * 100) / 100;
        row.reserve_amount = Math.max(0, Math.round((available - row.payout_amount) * 100) / 100);
      } else {
        row.reserve_amount = available;
        row.payout_amount = 0;
      }
      updated[index] = row;
      return updated;
    });
  };

  // T3-126: the WHT amount is operator-editable, defaulting to the computed figure. The COMPUTED
  // figure is kept separately (wht_computed_amount) and is what the API stamps onto the schedule
  // anchor row — so an override produces a contract_payment_variance row automatically and is
  // auditable. Editing the amount changes this row's net value, hence the re-split.
  const handleWhtAmountChange = (index, value) => {
    const num = Math.max(0, parseFloat(value) || 0);
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const priorRow = updated[index];
      // net_value is (principal + interest − wht), so changing the tax moves it by the delta.
      row.wht_amount = num;
      row.net_value = Math.round((priorRow.net_value + (priorRow.wht_amount || 0) - num) * 100) / 100;
      const available = allocatableOf(row);
      const prior = (priorRow.reserve_amount || 0) + (priorRow.payout_amount || 0);
      if (prior > 0.005) {
        const payoutShare = (priorRow.payout_amount || 0) / prior;
        row.payout_amount = Math.round(available * payoutShare * 100) / 100;
        row.reserve_amount = Math.max(0, Math.round((available - row.payout_amount) * 100) / 100);
      } else {
        row.reserve_amount = available;
        row.payout_amount = 0;
      }
      updated[index] = row;
      return updated;
    });
  };

  // T3-117: write-off for one investor. Clamped to that investor's share of the eligible residual —
  // it can be reduced or zeroed, never raised. The API re-reads the tenant settings and rejects
  // anything unauthorised regardless, so this clamp is for the operator's benefit, not the control.
  //
  // Changing the write-off RE-SPLITS the allocation, because the amount available has changed. The
  // existing suspense/payout SPLIT is preserved proportionally rather than reset to all-suspense, so
  // an operator who has already decided "60% payout" does not silently lose that decision by editing
  // the write-off afterwards.
  const handleWriteoffChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row.writeoff_amount = Math.max(0, Math.min(num, row.writeoff_max));

      const available = Math.max(0, row.net_value - row.writeoff_amount);
      const prior = (updated[index].reserve_amount || 0) + (updated[index].payout_amount || 0);
      if (prior > 0.005) {
        const payoutShare = (updated[index].payout_amount || 0) / prior;
        row.payout_amount = Math.round(available * payoutShare * 100) / 100;
        row.reserve_amount = Math.round((available - row.payout_amount) * 100) / 100;
      } else {
        row.reserve_amount = available;
        row.payout_amount = 0;
      }
      updated[index] = row;
      return updated;
    });
  };

  // Quick-action: all to suspense
  const setAllSuspense = () => {
    setInvestorRows((prev) =>
      prev.map((row) => ({ ...row, reserve_amount: allocatableOf(row), payout_amount: 0 }))
    );
  };

  // Quick-action: all paid out
  const setAllPayout = () => {
    setInvestorRows((prev) =>
      prev.map((row) => ({ ...row, reserve_amount: 0, payout_amount: allocatableOf(row) }))
    );
  };

  // Validation
  // Suspense + payout must equal the NET OF WRITE-OFF amount, not the gross net value. Validating
  // against net_value would accept an allocation that pays out the full amount while ALSO recording a
  // write-off — forgiving money and paying it out at the same time.
  const allocationValid = investorRows.length > 0 && investorRows.every((row) => {
    const sum = row.reserve_amount + row.payout_amount;
    return Math.abs(sum - allocatableOf(row)) <= 0.01;
  });
  const isValid = !!finalisationDate && allocationValid && !!reason;

  // Totals
  const totalCapital = investorRows.reduce((s, r) => s + r.original_capital, 0);
  const totalInterest = investorRows.reduce((s, r) => s + r.accrued_interest, 0);
  const totalWht = investorRows.reduce((s, r) => s + r.wht_amount, 0);
  const totalNetValue = investorRows.reduce((s, r) => s + r.net_value, 0);
  const totalSuspense = investorRows.reduce((s, r) => s + r.reserve_amount, 0);
  const totalPayout = investorRows.reduce((s, r) => s + r.payout_amount, 0);
  // T3-117: the Write-Off column is shown only when this contract actually has something eligible.
  // A column of zeros on every other contract would be noise; the "Write-Off" tile in the Still
  // Outstanding band above always states the position, including when there is nothing to write off.
  const totalWriteoffMax = investorRows.reduce((s, r) => s + (r.writeoff_max || 0), 0);
  const totalWriteoff = investorRows.reduce((s, r) => s + (r.writeoff_amount || 0), 0);
  const showWriteoffColumn = totalWriteoffMax > 0.005;

  // WHT treatment drives the tile wording. Matches the labels already used in
  // AmortizationScheduleTable / ApprovalDetailModal / excelExport: gross-up means the company bears
  // the tax and nothing is deducted from the investor.
  const hasGrossUp = investorRows.some((r) => r.wht_gross_up);

  // Submit
  const handleSubmit = async ({ proof_document_ids }) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        finalisation_date: finalisationDate,
        investor_allocations: investorRows.map((row) => ({
          // T3-117: each investor's own write-off, already clamped to their share of the eligible
          // residual by handleWriteoffChange. sp_contract_finalise reads writeoff_amount PER
          // allocation, so this stores per investor with no server change. The API independently
          // re-reads the tenant settings and rejects anything unauthorised.
          writeoff_amount: row.writeoff_amount || 0,
          investor_id: row.investor_id,
          original_capital: row.original_capital,
          accrued_interest: row.accrued_interest,
          wht_amount: row.wht_amount,
          wht_rate: row.wht_rate,
          wht_gross_up: row.wht_gross_up,
          // T3-126: how this investor's tax settles, and the reason for any override of the amount.
          // The API re-derives net_value / wht_computed_amount server-side and ignores whatever the
          // client sends for those — Case B pays out MORE than the computed net, so a
          // trusted-client gross would be exactly the wrong thing to accept.
          wht_treatment: row.wht_treatment || 'remit',
          wht_variance_reason:
            Math.abs((row.wht_amount || 0) - (row.wht_computed_amount || 0)) > 0.005
              ? (reason || null)
              : null,
          net_value: row.net_value,
          reserve_amount: row.reserve_amount,
          payout_amount: row.payout_amount,
        })),
        reason,
        proof_document_ids,
      };
      const response = await apiPost(`/contracts/${contractId}/finalise`, payload);
      onClose();
      if (onSubmitSuccess) onSubmitSuccess(response.data?.status === 'executed' ? 'executed' : 'pending');
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to submit finalisation');
    } finally {
      setSubmitting(false);
    }
  };

  const currencyCode = contract?.currency_code || 'ZAR';

  return (
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      workflowCode="contract_finalise"
      title="Contract Finalisation"
      description={`Finalise this contract by allocating each investor's net value to rollover suspense (held for future rollover) or payout (disbursement). ${contract?.status === 'Matured' ? 'This will be executed immediately.' : 'This will be submitted for approval.'}`}
      submitLabel={contract?.status === 'Matured' ? 'Finalise Contract' : 'Submit for Approval'}
      submitColor="primary"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      reason={reason}
      onReasonChange={setReason}
      // The allocation table carries seven columns of currency, and the T3-117 Write-Off column
      // pushed it past what "lg" (1200px) can hold — figures like "750 000.00" were wrapping mid-
      // number, which is unreadable in a financial table.
      maxWidth="xl"
    >
      {/* Finalisation Date */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Finalisation Date"
            type="date"
            fullWidth
            value={finalisationDate}
            onChange={(e) => setFinalisationDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd') }}
            helperText="Max 30 days from today"
          />
        </Grid>
      </Grid>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">Loading financial summary...</Typography>
        </Box>
      )}

      {/* Financial Summary */}
      {!loading && investorRows.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>Financial Summary</Typography>

            {/* T3-108. The breakdown below always adds up — "Earned since last payment date" is the
                balancing figure — so a wrong figure in any other part is absorbed into it and stays
                invisible. This warning is the only signal when that happens. Finalisation is NOT
                blocked: the interest total comes from a different calculation and is unaffected, so
                blocking would stop a legitimate payout because of a display fault. */}
            {residual && !residual.reconciled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The breakdown below could not be verified, so the figures may not be complete.
                The totals are still correct and this contract can be finalised. Please report this.
              </Alert>
            )}

            {/* T3-124. This contract's schedule rows are not split per investor, so each investor's
                Interest and WHT cannot be derived from their own rows and are estimated by capital
                share instead. The CONTRACT totals are still correct. Not blocked, for the same
                reason as the T3-108 warning above: a display fault must not stop a valid payout. */}
            {residual && !residual.attributionComplete && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                The per-investor Interest and Withholding Tax below are <strong>estimated</strong>,
                split by each investor&apos;s share of capital: {currencyCode}{' '}
                {formatWithSpaces(Math.abs(residual.unattributedInterest))} of interest on this
                contract&apos;s schedule is not assigned to a specific investor. The contract totals
                are correct and this contract can be finalised, but please check the per-investor
                split before paying out, and report this.
              </Alert>
            )}

            {/* T3-124. WHT is normally the provision the schedule itself carries on each interest
                line, less whatever has already been remitted — so tax already paid is never charged
                twice. Where a line carries NO provision (typically because the investor's WHT
                treatment changed during the life of the contract) there is nothing to read, so the
                rate is applied instead and shown here as a DECISION rather than being applied
                silently or dropped silently. */}
            {residual && residual.whtRateFallback > 0.005 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {currencyCode} {formatWithSpaces(residual.whtRateFallback)} of withholding tax may be
                due at the investor&apos;s rate on interest whose schedule lines carry no tax
                provision. It is <strong>not included</strong> in the figures below. Review the
                Withholding Tax column and action it if it should be withheld.
              </Alert>
            )}

            <BandLabel>Payable to investor</BandLabel>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Outstanding Capital</Typography>
                <Figure>{currencyCode} {formatNumber(totalCapital)}</Figure>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Accrued Interest</Typography>
                {residual ? (
                  <Box>
                  <Tooltip
                    arrow
                    placement="bottom-start"
                    title={
                      <Box sx={{ py: 0.5, minWidth: 240 }}>
                        <TipRow k="Interest still owed" v={formatWithSpaces(totalInterest)} bold />
                        <TipRule />
                        <TipRow k="Short paid" v={formatWithSpaces(residual.shortPaid)} />
                        <TipRow k="Not yet paid" v={formatWithSpaces(residual.notYetPaid)} />
                        {residual.accruedNotDue > 0.005 && (
                          <TipRow k="Earned since last payment date" v={formatWithSpaces(residual.accruedNotDue)} />
                        )}
                        {residual.whtNotYetPaid > 0.005 && (
                          <TipRow
                            k="Includes withholding tax"
                            v={formatWithSpaces(residual.whtNotYetPaid)}
                            indent
                          />
                        )}
                      </Box>
                    }
                  >
                    <Figure tabIndex={0} hint>{currencyCode} {formatNumber(totalInterest)}</Figure>
                  </Tooltip>
                  </Box>
                ) : (
                  <Figure>{currencyCode} {formatNumber(totalInterest)}</Figure>
                )}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">
                  {hasGrossUp ? 'Withholding Tax (Company Expense)' : 'Withholding Tax'}
                </Typography>
                <Figure color={totalWht > 0 ? 'error.main' : 'text.disabled'}>
                  ({currencyCode} {formatNumber(totalWht)})
                </Figure>
                {/* No sub-caption on either branch. "(Company Expense)" in the label already states
                    who bears the tax, so "Not Deducted" repeated the same fact from the investor's
                    side; on the non-gross-up branch the label plus the deducted figure say it too. */}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Total Net Value</Typography>
                {residual ? (
                  <Box>
                  <Tooltip
                    arrow
                    placement="bottom-end"
                    title={
                      <Box sx={{ py: 0.5, minWidth: 260 }}>
                        {/* Capital is DERIVED as net - interest + WHT rather than read from the
                            Outstanding Capital tile. On a MULTI-investor contract the two are not the
                            same number: the summary function emits contract_investors.capital_amount
                            as original_capital but builds net_value from a PRORATED
                            outstanding_principal, and on dev MTN-SZ-2026-0001 those differ by 98,000.
                            A tooltip that explains a total must add up to that total, so it derives
                            the balancing figure instead of importing a second one. The discrepancy
                            itself is a DB-side issue, recorded separately — this does not hide it,
                            the two tiles still show their own values side by side. */}
                        <TipRow
                          k="Outstanding capital"
                          v={formatWithSpaces(totalNetValue - totalInterest + totalWht)}
                        />
                        <TipRow k="Interest still owed" v={formatWithSpaces(totalInterest)} />
                        {/* Indented: these are what the interest above is MADE OF, not amounts to add
                            to it. That distinction is the whole point of this tooltip -- the "Still
                            outstanding" tiles below look like separate debts, and the natural reading
                            is that a short-paid line is owed ON TOP of the interest. It is not: it is
                            already inside it, which is why the components sum to the interest exactly. */}
                        {/* The "of which" lines are shown ONLY when they genuinely sum to the interest
                            above. On a few contracts they do not — the components exceed the interest
                            because cash was matched AHEAD of the finalisation date (OPEN-ITEM 55), so
                            dev c11 reads 78,102.74 of components against 66,102.74 of interest. Where
                            that holds, labelling them "of which" would be a false claim, so they are
                            simply omitted; residual_reconciled already flags the condition and the
                            tiles below still show each figure. */}
                        {residual.componentsReconcile && (
                          <>
                            {residual.shortPaid > 0.005 && (
                              <TipRow k="of which short paid" v={formatWithSpaces(residual.shortPaid)} indent />
                            )}
                            {residual.notYetPaid > 0.005 && (
                              <TipRow k="of which not yet paid" v={formatWithSpaces(residual.notYetPaid)} indent />
                            )}
                            {residual.accruedNotDue > 0.005 && (
                              <TipRow k="of which earned, not yet due" v={formatWithSpaces(residual.accruedNotDue)} indent />
                            )}
                          </>
                        )}
                        <TipRow
                          k={hasGrossUp ? 'Withholding tax (company expense)' : 'Withholding tax'}
                          v={`(${formatWithSpaces(totalWht)})`}
                        />
                        <TipRule />
                        <TipRow k="Total net value" v={formatWithSpaces(totalNetValue)} bold />
                        {/* Deliberately OUTSIDE the sum. Over-payment is NOT currently deducted from
                            the payout (see the warning below), so listing it as a line would make this
                            tooltip disagree with the total it is explaining. */}
                        {residual.overPaid > 0.005 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.9 }}>
                            Separately, {formatWithSpaces(residual.overPaid)} was received above the
                            scheduled amount and is not yet credited here.
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <Figure tabIndex={0} hint bold>{currencyCode} {formatNumber(totalNetValue)}</Figure>
                  </Tooltip>
                  </Box>
                ) : (
                  <Figure bold>{currencyCode} {formatNumber(totalNetValue)}</Figure>
                )}
              </Grid>
            </Grid>

            {residual && (
              <>
                <BandLabel sx={{ mt: 2 }}>Still outstanding</BandLabel>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Interest Short Paid</Typography>
                    <Figure color={residual.shortPaid > 0.005 ? 'error.main' : 'text.disabled'}>
                      {residual.shortPaid > 0.005 ? `${currencyCode} ${formatNumber(residual.shortPaid)}` : '—'}
                    </Figure>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Interest Not Yet Paid</Typography>
                    <Figure color={residual.notYetPaid > 0.005 ? 'error.main' : 'text.disabled'}>
                      {residual.notYetPaid > 0.005 ? `${currencyCode} ${formatNumber(residual.notYetPaid)}` : '—'}
                    </Figure>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Withholding Tax Not Yet Paid</Typography>
                    {residual.whtNotYetPaid > 0.005 ? (
                      <Box>
                      <Tooltip
                        arrow
                        placement="bottom-start"
                        title={
                          <Box sx={{ py: 0.5, maxWidth: 260 }}>
                            <Typography variant="caption">
                              Part of the interest not yet paid, not an extra amount.
                              {hasGrossUp
                                ? ' Paid by the company, not deducted from the investor.'
                                : ' It will be deducted from the investor when payment is made.'}
                            </Typography>
                          </Box>
                        }
                      >
                        <Figure tabIndex={0} hint color="warning.main">
                          {currencyCode} {formatNumber(residual.whtNotYetPaid)}
                        </Figure>
                      </Tooltip>
                      </Box>
                    ) : (
                      <Figure color="text.disabled">—</Figure>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Over Paid</Typography>
                    <Figure color={residual.overPaid > 0.005 ? 'warning.main' : 'text.disabled'}>
                      {residual.overPaid > 0.005 ? `${currencyCode} ${formatNumber(residual.overPaid)}` : '—'}
                    </Figure>
                  </Grid>
                  {/* T3-117. ALWAYS rendered, even when nothing is eligible. An earlier build showed
                      this only when a residual existed, which meant a contract with nothing to write
                      off gave no sign the feature existed at all — indistinguishable from it being
                      broken or unconfigured. The tile states which of the three it is. */}
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="textSecondary">Write-Off</Typography>
                    {residual.writeoffEligible > 0.005 ? (
                      <Figure color={residual.writeoffAmount > 0.005 ? 'info.main' : 'warning.main'}>
                        {currencyCode} {formatNumber(residual.writeoffEligible)}
                      </Figure>
                    ) : (
                      <Tooltip
                        arrow
                        placement="bottom-start"
                        title="Nothing to write off: no part-paid line on this contract is short. A line that received no payment at all is never eligible."
                      >
                        <Figure tabIndex={0} hint color="text.disabled">—</Figure>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>

                {/* OVER-PAYMENT: cash received ABOVE the scheduled amount on a line.
                    ⚠ This is NOT currently deducted from the payout. Each line's credit is capped at
                    its own scheduled amount (the T3-103 clamp, which correctly stops one line
                    discharging its neighbours), but the surplus is then credited nowhere — so the
                    investor is paid the full outstanding interest despite having already handed over
                    this money. Measured on dev Pro-US-2026-0001: 43,726.03 of interest cash received,
                    25,890.41 credited, 17,835.62 unaccounted.
                    Surfaced here rather than silently corrected: crediting it changes the payout, so
                    it is a deliberate money-moving decision (OPEN-ITEM 57), not a UI fix. */}
                {residual.overPaid > 0.005 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>{currencyCode} {formatNumber(residual.overPaid)}</strong> was received above
                    the scheduled amount on one or more lines. This is <strong>not</strong> deducted
                    from the payout below — the investor has already paid it, so check whether it
                    should be credited or refunded before finalising.
                  </Alert>
                )}

                {/* T3-117. Explains ONLY the cases where a residual exists but CANNOT be written off.
                    When it can be, the editable Write-Off column in the allocation table below is the
                    control — a banner as well would state the same thing twice. */}
                {/* Only when NOTHING may be written off — i.e. the tenant has the feature switched
                    off (or the maximum is 0.00). When a ceiling allows even part of the residual,
                    the editable Write-Off column below is the control and says so with its own
                    "Max n.nn" hint; a banner as well would state it twice. */}
                {residual.writeoffEligible > 0.005 && residual.writeoffAmount <= 0.005 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>{currencyCode} {formatNumber(residual.writeoffEligible)}</strong> is
                    outstanding on part-paid lines. Write-off is not enabled for this tenant, so it
                    will be carried in full.
                  </Alert>
                )}
              </>
            )}
          </Paper>

          {/* Quick actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Per-Investor Allocation</Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={setAllSuspense}>All to Suspense</Button>
              <Button onClick={setAllPayout}>All to Payout</Button>
            </ButtonGroup>
          </Box>

          {/* Per-investor allocation table */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            {/* nowrap on every cell: a currency figure broken across two lines ("750" / "000.00")
                is unreadable and easy to misread as a different number. Set once on the table so it
                covers the totals row as well; tabular-nums keeps the digit columns aligned. The
                container already scrolls horizontally if the viewport is genuinely too narrow. */}
            <Table
              size="small"
              sx={{
                '& td, & th': { whiteSpace: 'nowrap' },
                '& td': { fontVariantNumeric: 'tabular-nums' },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Investor</TableCell>
                  <TableCell align="right">Capital</TableCell>
                  <TableCell align="right">Interest</TableCell>
                  <TableCell align="right">WHT</TableCell>
                  <TableCell align="right">Net Value</TableCell>
                  {/* The cap is stated ONCE in this header, inline beside the label rather than on a
                      second line — it is a contract-level figure, so a per-row hint would repeat the
                      same number down the whole table on a contract with many investors. */}
                  {showWriteoffColumn && (
                    <TableCell align="right" sx={{ minWidth: 180 }}>
                      Write-Off
                      <Typography
                        component="span"
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontWeight: 400, ml: 0.75 }}
                      >
                        (max {formatWithSpaces(totalWriteoffMax)})
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ minWidth: 160 }}>Rollover Suspense</TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>Payout</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {investorRows.map((row, idx) => (
                  <TableRow key={row.investor_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{row.investor_name}</Typography>
                      {row.wht_rate > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          WHT: {row.wht_rate.toFixed(2)}%{row.wht_gross_up ? ' (gross-up)' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatWithSpaces(row.original_capital)}</TableCell>
                    <TableCell align="right">
                      {formatWithSpaces(row.accrued_interest)}
                      {/* T3-124: mark the ESTIMATED rows individually. The banner says the contract
                          has unassigned interest; this says which row to distrust — on a partially
                          attributed contract that distinction is real. */}
                      {residual && !residual.attributionComplete && (
                        <Typography variant="caption" color="warning.main" display="block">
                          estimated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {/* T3-126: the WHT is EDITABLE where there is tax to settle, and the operator
                          chooses whether we remit it or hand it to the investor for a third party.
                          Shown only where tax was actually withheld: a gross-up investor has none
                          (the payer absorbs it) and a 0%/no-provision row has nothing to settle. */}
                      {row.wht_amount > 0 || (!row.wht_gross_up && row.wht_computed_amount > 0) ? (
                        // ONE ROW: "Include in Payout: (o)  [ 6 750.00 ]" — label, then its radio,
                        // then the amount field, all inline.
                        //
                        // Stacking the treatment control UNDERNEATH the field changed the row height
                        // whenever it appeared, so the table jumped as the operator moved between
                        // investors. The cell already has horizontal room, so keeping everything on
                        // one fixed-height line holds the layout still. Same reason the override
                        // marker below is a separate caption rather than the input's helperText.
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip
                            arrow
                            title={
                              row.wht_treatment === 'payout'
                                ? 'INCLUDED in the payout — the tax is paid to the investor as part of the final payment and a third party remits it, so the payout goes GROSS. Click again to withhold and remit it ourselves.'
                                : 'Withheld and remitted by us, so the payout is NET. Select to include the tax in the payout instead, for a third party to remit.'
                            }
                          >
                            <FormControlLabel
                              sx={{ mr: 0, ml: 0 }}
                              // Radio to the RIGHT of the text, which is what the trailing colon
                              // on the label reads towards ("Include in Payout: (o)").
                              labelPlacement="start"
                              label={
                                <Typography variant="caption" color={row.wht_treatment === 'payout' ? 'info.main' : 'text.secondary'}>
                                  Include&nbsp;in&nbsp;Payout:
                                </Typography>
                              }
                              control={
                                <Radio
                                  size="small"
                                  sx={{ p: 0.25 }}
                                  checked={row.wht_treatment === 'payout'}
                                  // Selected = the tax is INCLUDED in the payout (Case B, gross);
                                  // cleared = we withhold and remit it (Case A, net — the default).
                                  // Clicking the selected radio clears it, so one control covers
                                  // both treatments without a second button changing the row height.
                                  onClick={() => handleWhtTreatmentChange(
                                    idx, row.wht_treatment === 'payout' ? 'remit' : 'payout')}
                                />
                              }
                            />
                          </Tooltip>
                          <CurrencyInput
                            size="small"
                            value={row.wht_amount}
                            onChange={(v) => handleWhtAmountChange(idx, v)}
                            sx={{ maxWidth: 120, '& input': { textAlign: 'right' } }}
                          />
                        </Box>
                      ) : (
                        row.wht_amount > 0 ? `(${formatWithSpaces(row.wht_amount)})` : '-'
                      )}
                      {/* The override marker sits with the other captions below, so it does not
                          resize the input row when it appears. */}
                      {Math.abs((row.wht_amount || 0) - (row.wht_computed_amount || 0)) > 0.005 && (
                        <Typography variant="caption" color="warning.main" display="block">
                          was {formatWithSpaces(row.wht_computed_amount)}
                        </Typography>
                      )}
                      {/* T3-124: tax owed at the investor's rate on lines carrying no provision.
                          Shown as a separate, clearly-excluded figure — never folded into the
                          charged amount above. */}
                      {row.wht_rate_fallback_amount > 0.005 && (
                        <Tooltip
                          arrow
                          title={`No tax provision is stored on some of this investor's interest lines. At ${row.wht_rate.toFixed(2)}% this would be ${formatWithSpaces(row.wht_rate_fallback_amount)}. It is NOT included in the figures shown.`}
                        >
                          <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                            sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                          >
                            +{formatWithSpaces(row.wht_rate_fallback_amount)} not applied
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{formatWithSpaces(row.net_value)}</Typography>
                    </TableCell>
                    {showWriteoffColumn && (
                      <TableCell align="right">
                        <CurrencyInput
                          size="small"
                          value={row.writeoff_amount}
                          onChange={(v) => handleWriteoffChange(idx, v)}
                          sx={{ '& input': { textAlign: 'right' } }}
                          // Each investor is capped at their OWN share of the residual, which is not
                          // the contract total shown in the header. On a multi-investor contract that
                          // difference matters, so the per-row cap is surfaced ONLY where it actually
                          // differs — silently reducing a typed figure would look like a bug.
                          helperText={
                            investorRows.length > 1 && row.writeoff_max < totalWriteoffMax - 0.005
                              ? `Max ${formatWithSpaces(row.writeoff_max)}`
                              : undefined
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        value={row.reserve_amount}
                        onChange={(v) => handleSuspenseChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        value={row.payout_amount}
                        onChange={(v) => handlePayoutChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalCapital)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalInterest)}</Typography></TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}><Typography variant="body2" fontWeight="bold">{totalWht > 0 ? `(${formatWithSpaces(totalWht)})` : '-'}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalNetValue)}</Typography></TableCell>
                  {showWriteoffColumn && (
                    <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="info.main">{formatWithSpaces(totalWriteoff)}</Typography></TableCell>
                  )}
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="primary">{formatWithSpaces(totalSuspense)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="warning.main">{formatWithSpaces(totalPayout)}</Typography></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary chips */}
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 3, justifyContent: 'flex-end', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary">Rollover Suspense</Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {currencyCode} {formatWithSpaces(totalSuspense)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Total Payout</Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {currencyCode} {formatWithSpaces(totalPayout)}
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {/* No investors */}
      {!loading && investorRows.length === 0 && finalisationDate && !error && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          No investor data available. Please check the finalisation date.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
    </ContractActionDialogBase>
  );
};

export default FinalisationDialog;
